import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Inbox, UserPlus, Trash2, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, formatDateTime } from "../lib/api";
import { useT } from "../i18n/I18nContext";

const STATUS_OPTIONS = ["new", "contacted", "approved", "rejected", "converted"];

function statusClass(s) {
  switch (s) {
    case "new": return "bg-[#D4AF37]/15 text-[#7A6420]";
    case "contacted": return "bg-blue-50 text-blue-800";
    case "approved": return "bg-emerald-50 text-emerald-800";
    case "converted": return "bg-[#0F4C3A]/10 text-[#0F4C3A]";
    case "rejected": return "bg-red-50 text-red-800";
    default: return "bg-slate-100 text-slate-700";
  }
}

function suggestUsername(firstname, lastname) {
  const slug = (s) => s.toLowerCase().normalize("NFD").replace(/[^a-z0-9]/g, "");
  return `${slug(firstname || "")}.${slug(lastname || "")}`.replace(/^\.|\.$/g, "") || "";
}

function randomPwd() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  let out = "";
  for (let i = 0; i < 10; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out + "!";
}

export default function AdminLeadsPage() {
  const t = useT();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [convertLead, setConvertLead] = useState(null);
  const [form, setForm] = useState({ username: "", password: "", name: "", phone: "", role: "participant" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      toast.success(t("admin.leads.toast.status"));
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeLead = async (id) => {
    if (!window.confirm(t("admin.leads.confirm_delete"))) return;
    try {
      await api.delete(`/leads/${id}`);
      toast.success(t("admin.leads.toast.deleted"));
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openConvert = (lead) => {
    setConvertLead(lead);
    setForm({
      username: suggestUsername(lead.firstname, lead.lastname),
      password: randomPwd(),
      name: `${lead.firstname} ${lead.lastname}`.trim(),
      phone: lead.phone || "",
      role: "participant",
      credential_delivery: "personal",
    });
  };

  const submitConvert = async () => {
    if (!form.username || form.username.length < 3 || !form.password || form.password.length < 6 || !form.name) {
      toast.error(t("interest.error.required"));
      return;
    }
    setSaving(true);
    try {
      await api.post("/users", {
        username: form.username.trim().toLowerCase(),
        password: form.password,
        name: form.name.trim(),
        phone: form.phone || null,
        role: form.role,
        email: convertLead?.email || null,
        lead_id: convertLead?.id,
        credential_delivery: form.credential_delivery,
      });
      toast.success(t("admin.leads.created_user"));
      setConvertLead(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
      <div className="flex items-center gap-3 mb-2">
        <Inbox className="w-6 h-6 text-[#0F4C3A]" />
        <h1 className="font-heading text-2xl md:text-3xl font-medium text-[#0F4C3A]" data-testid="admin-leads-title">
          {t("admin.leads.title")}
        </h1>
      </div>
      <p className="text-[#475569] mb-8">{t("admin.leads.subtitle")}</p>

      {loading ? (
        <div className="text-center py-16 text-[#94A3B8]">{t("common.loading")}</div>
      ) : leads.length === 0 ? (
        <div className="border border-dashed border-[#E2E8E4] rounded-md p-12 text-center text-[#94A3B8]" data-testid="admin-leads-empty">
          {t("admin.leads.empty")}
        </div>
      ) : (
        <div className="space-y-3">
          {leads.map((l) => (
            <div key={l.id} data-testid={`lead-row-${l.id}`} className="bg-white border border-[#E2E8E4] rounded-md p-5 hover:border-[#0F4C3A]/40 transition">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-heading text-lg font-medium text-[#0F4C3A]">
                      {l.firstname} {l.lastname}
                    </span>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded ${statusClass(l.status)}`}>
                      {t(`admin.leads.status.${l.status}`)}
                    </span>
                    {l.contact_request && (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-[#D4AF37]/20 text-[#7A6420]">
                        ☎ Kontakt erwünscht
                      </span>
                    )}
                  </div>
                  <div className="mt-2 text-sm text-[#475569] flex flex-wrap gap-x-5 gap-y-1">
                    <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {l.phone}</span>
                    {l.email && (<span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {l.email}</span>)}
                    {l.location && <span>{l.location}</span>}
                    <span className="text-[#94A3B8]">{formatDateTime(l.created_at)}</span>
                  </div>
                  {(l.beteiligung || l.anzahl_anteile) && (
                    <div className="mt-2 text-sm text-[#0F4C3A]">
                      {l.beteiligung && <span className="mr-3">{t("admin.leads.col.beteiligung")}: <strong>{l.beteiligung === "individuell" ? t("interest.beteiligung.individuell") : `CHF ${l.beteiligung}.–`}</strong></span>}
                      {l.anzahl_anteile && <span>{t("interest.anteile")}: <strong>{l.anzahl_anteile === "individuell" ? t("interest.anteile.individuell") : l.anzahl_anteile}</strong></span>}
                    </div>
                  )}
                  {l.message && (
                    <p className="mt-3 text-sm text-[#475569] bg-[#F8FAF9] border-l-2 border-[#D4AF37] pl-4 py-2 italic">
                      "{l.message}"
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-2 items-stretch">
                  <select
                    value={l.status}
                    onChange={(e) => updateStatus(l.id, e.target.value)}
                    data-testid={`lead-status-${l.id}`}
                    disabled={l.status === "converted"}
                    className="px-3 py-2 text-sm border border-[#E2E8E4] rounded-md bg-white"
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{t(`admin.leads.status.${s}`)}</option>
                    ))}
                  </select>
                  {l.status !== "converted" && (
                    <button
                      onClick={() => openConvert(l)}
                      data-testid={`lead-convert-${l.id}`}
                      className="nv-btn-primary px-3 py-2 rounded-md text-sm font-medium inline-flex items-center justify-center gap-1.5"
                    >
                      <UserPlus className="w-4 h-4" /> {t("admin.leads.create_user")}
                    </button>
                  )}
                  <button
                    onClick={() => removeLead(l.id)}
                    data-testid={`lead-delete-${l.id}`}
                    className="px-3 py-2 rounded-md text-sm border border-[#E2E8E4] text-[#94A3B8] hover:text-red-700 hover:border-red-300 inline-flex items-center justify-center gap-1.5"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {convertLead && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => !saving && setConvertLead(null)}>
          <div className="bg-white rounded-md p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-xl text-[#0F4C3A]">{t("admin.leads.modal.title")}</h3>
            <p className="text-sm text-[#475569] mt-1">{t("admin.leads.modal.subtitle")}</p>
            <div className="mt-5 space-y-3">
              <Field label={t("admin.leads.modal.name")}>
                <input
                  data-testid="convert-name"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="nv-input"
                />
              </Field>
              <Field label={t("admin.leads.modal.username")}>
                <input
                  data-testid="convert-username"
                  value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })}
                  placeholder={t("admin.leads.modal.username_placeholder")}
                  className="nv-input"
                />
              </Field>
              <Field label={t("admin.leads.modal.password")}>
                <input
                  data-testid="convert-password"
                  value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="nv-input font-mono-nv"
                />
              </Field>
              <Field label={t("admin.leads.modal.phone")}>
                <input
                  value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="nv-input"
                />
              </Field>
              <Field label={t("admin.users.delivery")}>
                <select
                  data-testid="convert-delivery"
                  value={form.credential_delivery} onChange={(e) => setForm({ ...form, credential_delivery: e.target.value })}
                  className="nv-input"
                >
                  <option value="email">{t("delivery.email")}</option>
                  <option value="phone">{t("delivery.phone")}</option>
                  <option value="personal">{t("delivery.personal")}</option>
                </select>
              </Field>
              <Field label={t("admin.leads.modal.role")}>
                <select
                  value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="nv-input"
                >
                  <option value="participant">{t("role.participant")}</option>
                  <option value="admin">{t("role.admin")}</option>
                  <option value="controller">{t("role.controller")}</option>
                </select>
              </Field>
            </div>
            <div className="flex gap-3 pt-5">
              <button
                onClick={submitConvert}
                disabled={saving}
                data-testid="convert-submit"
                className="flex-1 nv-btn-primary py-2.5 rounded-md disabled:opacity-50"
              >
                {saving ? t("common.loading") : t("admin.leads.modal.create")}
              </button>
              <button
                onClick={() => setConvertLead(null)}
                disabled={saving}
                className="flex-1 py-2.5 border border-[#E2E8E4] rounded-md"
              >
                {t("common.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="nv-label block mb-1">{label}</span>
      {children}
    </label>
  );
}
